import {
  ReactElement,
  useCallback,
  useRef,
  useState,
} from 'react';

import { Box, CircularProgress } from '@mui/material';
import { formatTranslation } from 'intl';
import { CommonTranslationKeys } from 'intl/keys';
import { ErrorBoundary } from '@sentry/react';
import { useIntl } from 'react-intl';

import useDebounce from 'hooks/useDebounce';
import useResize from 'hooks/useResize';
import {
  ButtonContained,
  ButtonIcon,
  ButtonText,
} from 'components/general';
import { useBlobFileStorage } from 'components/core/BlobFileStorage';
import { BrushOutlined as DrawingIcon } from '@mui/icons-material';

import { ClearIcon, UndoIcon } from '../icons';
import { showErrorToastAndLog } from 'services/toaster';
import { DOCUMENT_ERROR_SOURCE } from 'shared/domain/document/documentError';
import { logError } from 'setup/logger/logError';
import {
  canvasService,
  drawingService,
  rotationService,
  signedRequestService,
} from 'services/fileService';

import {
  DEBOUNCE_TIME,
  MIN_SCALE_FACTOR,
  ZOOM_FACTOR,
  fullyCleared,
  getInitialScale,
} from './model';
import { DrawProps } from './types';
import { PaintingCanvas } from './components/PaintingCanvas';
import {
  DRAW_BUTTONS_SX,
  DRAW_FOOTER_SX,
  getDrawContainerSx,
} from './styles';
import useCanvasControls from './hooks/useCanvasControls';
import useImageSource from './hooks/useImageSource';

const Draw = ({
  file,
  container,
  setDrawMode,
  addDrawing,
  idx,
  drawing,
}: DrawProps): ReactElement | null => {
  const processingRef = useRef(false);
  const resize = useResize();
  const intl = useIntl();
  const blobFileStorage = useBlobFileStorage();
  const windowSize = useDebounce(resize, DEBOUNCE_TIME);
  const initiallyCleared = drawing?.clear || false;
  const hasMergedBackgroundSource =
    !initiallyCleared &&
    signedRequestService.hasMergedBackgroundSource(file, drawing);
  const initialDrawing = hasMergedBackgroundSource ? '' : drawing?.data || '';
  const [processing, _setProcessing] = useState(false);
  const [isPainting, setIsPainting] = useState(false);
  const [cleared, setCleared] = useState(initiallyCleared);

  const onTogglePainting = useCallback(() => {
    setIsPainting((prev) => !prev);
  }, []);

  const onClear = useCallback(() => {
    setCleared(true);
  }, []);

  const setProcessing = useCallback((value: boolean): void => {
    processingRef.current = value;
    _setProcessing(value);
  }, []);

  const drawModeOff = useCallback(() => {
    setDrawMode(false);
  }, [setDrawMode]);

  const { src, dimensions, imageNaturalDimensions } = useImageSource({
    file,
    drawing,
    cleared,
    container,
    windowSize,
  });

  const {
    canvasDrawElementRef,
    setCanvasRef,
    stepBack,
    reset,
    getData,
    toggleDrawing,
  } = useCanvasControls({
    initialDrawing,
    onTogglePainting,
    onClear,
    src,
    windowSize,
    paintCanvasWidth: imageNaturalDimensions.width,
    paintCanvasHeight: imageNaturalDimensions.height,
    drawModeOff,
  });

  const initialScale = getInitialScale(dimensions, imageNaturalDimensions);
  const minScale = initialScale * MIN_SCALE_FACTOR;
  const maxScale = initialScale * ZOOM_FACTOR;

  const onSave = useCallback(() => {
    if (!canvasDrawElementRef.current) {
      showErrorToastAndLog('No canvas draw element');
      setProcessing(false);
      drawModeOff();
      return;
    }

    if (processingRef.current) {
      return;
    }
    setProcessing(true);

    if (cleared || drawing?.clear) {
      const clearedPreviewSource =
        signedRequestService.resolveDrawClearedPreviewSource(file) ||
        DOCUMENT_ERROR_SOURCE;
      file.src = clearedPreviewSource;
      file.thumbnail = clearedPreviewSource;
    }

    const lines = getData();
    const linesObject: boolean | number | { lines: any[] } = JSON.parse(
      `${Boolean(lines)}` && `${lines.length}` && lines
    );

    if (fullyCleared(cleared, linesObject)) {
      addDrawing({ clear: true }, idx);
      if (drawing?.mergedImageUrl) {
        blobFileStorage.removeItem(drawing.mergedImageUrl);
      }
      drawModeOff();
      setProcessing(false);
      file.downloadSrc = signedRequestService.resolvePostClearDownloadSource(file);
      return;
    }

    const drawingCanvasPromise = drawingService.createDrawingCanvas(
      cleared || drawing?.clear,
      canvasDrawElementRef.current.ctx.drawing.canvas,
      file,
      drawing
    );

    const originalImageSrc = signedRequestService.resolveDrawOriginalSource(
      file,
      Boolean(cleared)
    );
    if (!originalImageSrc) {
      logError('Error when saving a drawing', new Error('Missing image source'));
      drawModeOff();
      setProcessing(false);
      showErrorToastAndLog('Error when saving a drawing',
        new Error('Missing image source')
      );
      return;
    }

    const originalImagePromise = canvasService.loadImageFromSource(originalImageSrc);

    return Promise.all([drawingCanvasPromise, originalImagePromise])
      .then(([drawingCanvas, originalImage]) => {
        const normalizedDrawingCanvas =
          rotationService.normalizeDrawingCanvasByImageRotation(
            drawingCanvas,
            file.imageRotation
          );
        const { original: mergedWithOriginal, thumbnail } =
          canvasService.createMergedImageCanvas(
            originalImage,
            normalizedDrawingCanvas
          );

        return Promise.all([
          canvasService.canvasToBlob(mergedWithOriginal),
          canvasService.canvasToBlob(normalizedDrawingCanvas),
          canvasService.canvasToBlob(thumbnail),
        ]).then(([mergedBlob, drawingBlob, thumbnailBlob]) => {
          const mergedImageUrl = canvasService.toPersistedImageSource(
            mergedWithOriginal,
            mergedBlob
          );
          const thumbnailMergedImageUrl = canvasService.toPersistedImageSource(
            thumbnail,
            thumbnailBlob
          );
          const drawingImageUrl = canvasService.toPersistedImageSource(
            normalizedDrawingCanvas,
            drawingBlob
          );
          addDrawing(
            {
              clear: cleared || drawing?.clear,
              data: lines,
              blob: drawingBlob,
              imageUrl: drawingImageUrl,
              mergedImageUrl,
              thumbnailMergedImageUrl,
              drawingImageUrl,
            },
            idx
          );
          setProcessing(false);
          drawModeOff();
        });
      })
      .catch((err) => {
        logError('Error when saving a drawing', err);
        drawModeOff();
        setProcessing(false);
        showErrorToastAndLog('Error when saving a drawing',
          err
        );
      });
  }, [
    addDrawing,
    blobFileStorage,
    cleared,
    drawModeOff,
    drawing,
    file,
    getData,
    idx,
    setProcessing,
  ]);

  if (
    !dimensions.width ||
    !dimensions.height ||
    !imageNaturalDimensions.width ||
    !imageNaturalDimensions.height
  ) {
    return (
      <Box sx={getDrawContainerSx(dimensions, 'crosshair')}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={getDrawContainerSx(
          dimensions,
          isPainting ? 'crosshair' : 'grab'
        )}
      >
        <ErrorBoundary
          fallback={
            <Box sx={getDrawContainerSx(dimensions, 'crosshair')}>
              <CircularProgress size={20} />
            </Box>
          }
          onError={(error) => {
            logError('CanvasDraw crashed in Draw component', error);
            drawModeOff();
          }}
        >
          <PaintingCanvas
            dimensions={dimensions}
            canvasWidth={imageNaturalDimensions.width}
            canvasHeight={imageNaturalDimensions.height}
            isPainting={isPainting}
            setCanvasRef={setCanvasRef}
            src={src}
            initialScale={initialScale}
            minScale={minScale}
            maxScale={maxScale}
          />
        </ErrorBoundary>
      </Box>
      <Box sx={DRAW_BUTTONS_SX}>
        <Box className='button'>
          <ButtonIcon value='undo' onClick={stepBack}>
            <UndoIcon />
          </ButtonIcon>
        </Box>
        <Box className='button'>
          <ButtonIcon value='clear' onClick={reset}>
            <ClearIcon />
          </ButtonIcon>
        </Box>
        <Box className='toggle'>
          <ButtonIcon
            onClick={toggleDrawing}
            className={isPainting ? 'active' : undefined}
          >
            <DrawingIcon />
          </ButtonIcon>
        </Box>
      </Box>
      <Box sx={DRAW_FOOTER_SX}>
        <ButtonText onClick={drawModeOff}>
          {formatTranslation(intl, CommonTranslationKeys.cancel)}
        </ButtonText>
        <ButtonContained
          disabled={processing}
          color='primary'
          onClick={onSave}
          endIcon={processing ? <CircularProgress size={16} /> : undefined}
        >
          {formatTranslation(intl, CommonTranslationKeys.save)}
        </ButtonContained>
      </Box>
    </>
  );
};

export default Draw;
